import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import { visuallyHidden } from "@mui/utils";
import toast from "react-hot-toast";
import MainCard from "../../../components/MainCard";
import {
  getInvoiceVatAnalysisApi,
  getInvoiceVatAnalysisDetailsApi,
} from "../../../components/api/InvoiceApiService";

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const fmtHuf = new Intl.NumberFormat("hu-HU", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const COLUMNS = [
  { id: "period", label: "Period", align: "left" },
  { id: "receivableVatHuf", label: "Receivable VAT (HUF)", align: "right" },
  { id: "payableVatHuf", label: "Payable VAT (HUF)", align: "right" },
  { id: "netVatHuf", label: "Net VAT (HUF)", align: "right" },
];

function fmt(val) {
  if (val == null) return "—";
  const n = Number(val);
  return Number.isFinite(n) ? fmtHuf.format(n) : "—";
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => (b[orderBy] < a[orderBy] ? -1 : b[orderBy] > a[orderBy] ? 1 : 0)
    : (a, b) => (a[orderBy] < b[orderBy] ? -1 : a[orderBy] > b[orderBy] ? 1 : 0);
}

export default function VatAnalysisView() {
  const currentYear = new Date().getFullYear();
  const [mode, setMode] = useState("MONTHLY");
  const [year, setYear] = useState(currentYear);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("period");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailRows, setDetailRows] = useState([]);
  const [selectedPeriodLabel, setSelectedPeriodLabel] = useState("");
  const [detailTotals, setDetailTotals] = useState({ receivableVatHuf: 0, payableVatHuf: 0, vatAmountHuf: 0 });

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      const params = mode === "MONTHLY"
        ? { mode, year }
        : { mode };
      const { data } = await getInvoiceVatAnalysisApi(params);
      const enriched = (Array.isArray(data) ? data : []).map((r) => ({
        ...r,
        period: mode === "MONTHLY"
          ? `${r.year}-${String(r.month).padStart(2, "0")}`
          : String(r.year ?? ""),
      }));
      setRows(enriched);
      setPage(0);
    } catch (e) {
      const msg = e?.response?.data?.message ?? e?.response?.data ?? "Failed to load VAT report.";
      toast.error(String(msg));
    } finally {
      setLoading(false);
    }
  }, [mode, year]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        acc.receivableVatHuf += Number(row.receivableVatHuf || 0);
        acc.payableVatHuf += Number(row.payableVatHuf || 0);
        acc.netVatHuf += Number(row.netVatHuf || 0);
        return acc;
      },
      { receivableVatHuf: 0, payableVatHuf: 0, netVatHuf: 0 }
    );
  }, [rows]);

  const sorted = useMemo(() => {
    return [...rows].sort(getComparator(order, orderBy));
  }, [rows, order, orderBy]);

  const paginated = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  async function handleOpenDetails(row) {
    try {
      setDetailLoading(true);
      setDetailOpen(true);

      const periodLabel = mode === "MONTHLY"
        ? `${MONTH_NAMES[(Number(row.month) || 1) - 1]} ${row.year}`
        : `${row.year}`;
      setSelectedPeriodLabel(periodLabel);

      const params = mode === "MONTHLY"
        ? { year: Number(row.year), month: Number(row.month) }
        : { year: Number(row.year) };

      const { data } = await getInvoiceVatAnalysisDetailsApi(params);
      const mapped = Array.isArray(data) ? data : [];
      setDetailRows(mapped);

      const totals = mapped.reduce(
        (acc, item) => {
          acc.receivableVatHuf += Number(item.receivableVatHuf || 0);
          acc.payableVatHuf += Number(item.payableVatHuf || 0);
          acc.vatAmountHuf += Number(item.vatAmountHuf || 0);
          return acc;
        },
        { receivableVatHuf: 0, payableVatHuf: 0, vatAmountHuf: 0 }
      );
      setDetailTotals(totals);
    } catch (e) {
      const msg = e?.response?.data?.message ?? e?.response?.data ?? "Failed to load VAT detail.";
      toast.error(String(msg));
      setDetailRows([]);
      setDetailTotals({ receivableVatHuf: 0, payableVatHuf: 0, vatAmountHuf: 0 });
    } finally {
      setDetailLoading(false);
    }
  }

  function renderDetailDate(value) {
    if (!value) return "—";
    return new Date(value).toLocaleDateString("en-GB");
  }

  function renderPeriod(row) {
    if (mode === "MONTHLY") {
      const monthIdx = Number(row.month) - 1;
      const monthLabel = monthIdx >= 0 && monthIdx < 12 ? MONTH_NAMES[monthIdx] : "—";
      return `${monthLabel} ${row.year}`;
    }
    return `${row.year ?? "—"}`;
  }

  return (
    <MainCard>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, px: 1 }}>
        <Stack spacing={0.3}>
          <Typography variant="h5" fontWeight={700} color="#1E2685">
            VAT Analysis
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Purchase invoices generate receivable VAT, sales invoices generate payable VAT.
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup
            size="small"
            exclusive
            value={mode}
            onChange={(_, v) => v && setMode(v)}
          >
            <ToggleButton value="MONTHLY">Monthly</ToggleButton>
            <ToggleButton value="YEARLY">Yearly</ToggleButton>
          </ToggleButtonGroup>
          <IconButton size="small" onClick={fetchRows} disabled={loading} title="Refresh">
            <RefreshIcon />
          </IconButton>
        </Stack>
      </Stack>

      <Grid container spacing={1.5} sx={{ px: 1, mb: 2 }}>
        <Grid item xs={12} md={3}>
          <TextField
            select
            size="small"
            fullWidth
            label="Year"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            disabled={mode !== "MONTHLY"}
          >
            {Array.from({ length: 11 }).map((_, idx) => {
              const y = currentYear - 5 + idx;
              return <MenuItem key={y} value={y}>{y}</MenuItem>;
            })}
          </TextField>
        </Grid>
        <Grid item xs={12} md={9}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ height: "100%" }}>
            <Typography variant="body2" color="text.secondary">
              {mode === "MONTHLY"
                ? `Showing 12 months of ${year}`
                : "Showing all years"}
            </Typography>
          </Stack>
        </Grid>
      </Grid>

      <Grid container spacing={1.5} sx={{ px: 1, pb: 2 }}>
        <Grid item xs={12} md={4}>
          <Box sx={{ border: "1px solid #dbe4ff", borderRadius: 2, p: 1.5 }}>
            <Typography variant="caption" color="text.secondary">Receivable VAT (HUF)</Typography>
            <Typography variant="h6" sx={{ color: "#1565c0", fontWeight: 700 }}>{fmt(totals.receivableVatHuf)}</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box sx={{ border: "1px solid #dbe4ff", borderRadius: 2, p: 1.5 }}>
            <Typography variant="caption" color="text.secondary">Payable VAT (HUF)</Typography>
            <Typography variant="h6" sx={{ color: "#2e7d32", fontWeight: 700 }}>{fmt(totals.payableVatHuf)}</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box sx={{ border: "1px solid #dbe4ff", borderRadius: 2, p: 1.5 }}>
            <Typography variant="caption" color="text.secondary">Net VAT (HUF)</Typography>
            <Typography
              variant="h6"
              sx={{ color: totals.netVatHuf >= 0 ? "#ef6c00" : "#6a1b9a", fontWeight: 700 }}
            >
              {fmt(totals.netVatHuf)}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {COLUMNS.map((col) => (
                <TableCell
                  key={col.id}
                  align={col.align}
                  sx={{ fontWeight: 700, whiteSpace: "nowrap", backgroundColor: "#f5f5f5" }}
                >
                  <TableSortLabel
                    active={orderBy === col.id}
                    direction={orderBy === col.id ? order : "asc"}
                    onClick={() => {
                      setOrder((prev) => (orderBy === col.id && prev === "asc" ? "desc" : "asc"));
                      setOrderBy(col.id);
                    }}
                  >
                    {col.label}
                    {orderBy === col.id && (
                      <Box component="span" sx={visuallyHidden}>
                        {order === "desc" ? "sorted descending" : "sorted ascending"}
                      </Box>
                    )}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Loading…</Typography>
                </TableCell>
              </TableRow>
            ) : paginated.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No VAT data found.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginated.map((row, idx) => (
                <TableRow
                  key={`${row.year}-${row.month ?? "y"}-${idx}`}
                  hover
                  onClick={() => handleOpenDetails(row)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#1E2685" }}>
                        {renderPeriod(row)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        (click for details)
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell align="right" sx={{ color: "#1565c0", fontWeight: 600 }}>{fmt(row.receivableVatHuf)}</TableCell>
                  <TableCell align="right" sx={{ color: "#2e7d32", fontWeight: 600 }}>{fmt(row.payableVatHuf)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(row.netVatHuf)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[25, 50, 100]}
        component="div"
        count={sorted.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack spacing={0.3}>
              <Typography variant="h6" fontWeight={700} color="#1E2685">
                VAT Detail - {selectedPeriodLabel}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Invoice-level VAT breakdown for the selected period.
              </Typography>
            </Stack>
            <IconButton size="small" onClick={() => setDetailOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={1.5} sx={{ pb: 2 }}>
            <Grid item xs={12} md={4}>
              <Box sx={{ border: "1px solid #dbe4ff", borderRadius: 2, p: 1.2 }}>
                <Typography variant="caption" color="text.secondary">Receivable VAT (HUF)</Typography>
                <Typography variant="subtitle1" sx={{ color: "#1565c0", fontWeight: 700 }}>{fmt(detailTotals.receivableVatHuf)}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ border: "1px solid #dbe4ff", borderRadius: 2, p: 1.2 }}>
                <Typography variant="caption" color="text.secondary">Payable VAT (HUF)</Typography>
                <Typography variant="subtitle1" sx={{ color: "#2e7d32", fontWeight: 700 }}>{fmt(detailTotals.payableVatHuf)}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ border: "1px solid #dbe4ff", borderRadius: 2, p: 1.2 }}>
                <Typography variant="caption" color="text.secondary">Total VAT (HUF)</Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{fmt(detailTotals.vatAmountHuf)}</Typography>
              </Box>
            </Grid>
          </Grid>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, backgroundColor: "#f5f5f5" }}>Invoice #</TableCell>
                  <TableCell sx={{ fontWeight: 700, backgroundColor: "#f5f5f5" }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700, backgroundColor: "#f5f5f5" }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700, backgroundColor: "#f5f5f5" }}>Stakeholder</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: "#f5f5f5" }}>Amount (HUF)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: "#f5f5f5" }}>VAT %</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: "#f5f5f5" }}>VAT (HUF)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: "#f5f5f5" }}>Receivable VAT</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: "#f5f5f5" }}>Payable VAT</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detailLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">Loading detail…</Typography>
                    </TableCell>
                  </TableRow>
                ) : detailRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">No invoice details found for this period.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  detailRows.map((item) => (
                    <TableRow key={item.invoiceId} hover>
                      <TableCell>{item.invoiceNumber ?? "—"}</TableCell>
                      <TableCell>{renderDetailDate(item.invoiceDate)}</TableCell>
                      <TableCell>{item.invoiceType ?? "—"}</TableCell>
                      <TableCell>{item.stakeholderName ?? "—"}</TableCell>
                      <TableCell align="right">{fmt(item.amountHuf)}</TableCell>
                      <TableCell align="right">{fmt(item.vatRatePercent)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(item.vatAmountHuf)}</TableCell>
                      <TableCell align="right" sx={{ color: "#1565c0" }}>{fmt(item.receivableVatHuf)}</TableCell>
                      <TableCell align="right" sx={{ color: "#2e7d32" }}>{fmt(item.payableVatHuf)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    </MainCard>
  );
}
